import { ExtractedRecordError, ExtractedRecord, TargetSchema, ValidAny } from "@/types";

/**
 * Get all schemas associated with a record.
 * Returns unique schemas derived from the record's extracted_fields.
 */
export const getRecordSchemas = (record: ExtractedRecord): TargetSchema[] => {
  if (!record.extracted_fields) return [];
  return Array.from(new Set(record.extracted_fields.map((field) => field.target_schema)));
};

/**
 * Get data from a record, optionally filtering by schema.
 * Constructs a simple key-value map from the ExtractedFields.
 */
export const getRecordData = (record: ExtractedRecord, schema?: TargetSchema): Record<string, ValidAny> => {
  if (!record.extracted_fields) return {};
  return record.extracted_fields
    .filter((field) => !schema || field.target_schema === schema)
    .reduce(
      (acc, field) => {
        acc[field.target_field] = field.extracted_value;
        return acc;
      },
      {} as Record<string, ValidAny>
    );
};

/**
 * Get unused data from a record
 */
export const getUnusedData = (record: ExtractedRecord): Record<string, ValidAny> => {
  return record.unused_data || {};
};

/** Which carrier-reported claim-type component(s) a document's YTD asserts. */
export type ReportedScope = "medical" | "rx" | "combined";

/**
 * Parse a carrier-reported YTD cell to a number, mirroring the BE tolerant
 * parser `ParseYTDAmount` (reported_ytd_scope.go): strip `$`, `,`, and ALL
 * whitespace — including internal, so space-separated thousands
 * (`"1 234.56"`) parse — then require a finite number.
 *
 * The Go side only trims surrounding whitespace, so it rejected `"1 234.56"`
 * until !4676 was fixed to match; that divergence was found in review
 * (`[AEM-parse-drift]`). Keep the two in step: a value one accepts and the
 * other drops is a YTD that silently falls out of reported authority. Non-numeric sentinels the carrier
 * sheets use for "not reported" — `"N/A"`, `"-"`, `"pending"`, a lone `"$"`,
 * whitespace-only — return null (NOT mapped), so the FE classifies scope the
 * same way the loader stamps authority. A genuine `0` parses to 0 (mapped).
 */
const parseYtd = (v: ValidAny): number | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[$,\s]/g, "");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/**
 * Which carrier-reported YTD components a document maps, plus the ADR-077 §8
 * double-count signal. Mirrors `document_processing.ReportedYTDSummary`.
 */
export interface ReportedYTDFlags {
  has_medical: boolean;
  has_rx: boolean;
  has_total: boolean;
  combined_total_risk: boolean;
}

/**
 * Compute the document's reported-YTD flags from a COMPLETE record set.
 *
 * ONLY valid when every record is in hand. Since #6755 the review surface pages,
 * so the review path must read the server's `record_summary.reported_ytd`
 * instead — see #6903: this function scanning a loaded page concluded "no YTD is
 * mapped", the paid-through-date control never rendered, and the reported side
 * won claims authority with the reviewer never offered the decision.
 *
 * Retained as the fallback for transformations written before #6755 (loaded
 * whole, so the scan is correct) and as the reference the Go implementation is
 * pinned against. `DetectDocumentReportedYTD` in
 * apps/pkg/domains/document_processing/reported_ytd_scope.go must agree with it.
 *
 * Unlike `max_rows_per_claimant`, this field IS mirrorable exactly — presence
 * plus a numeric comparison, with no name normalizer behind it — so the mirror
 * computes it rather than abstaining. Abstaining would fail open on a
 * pre-#6755 document with no YTD and raise a spurious "maps a carrier YTD
 * total" warning.
 *
 * UNION OF FLAGS across records, not of per-record verdicts: with record A
 * mapping only a total and record B only Rx, the flag union says HasTotal &&
 * HasRx so the bare total no longer stands in for medical ("rx"), where a
 * verdict union would say "combined". The flag union is the long-standing
 * behaviour and the Go side matches it.
 */
export const detectReportedYTD = (records: ExtractedRecord[]): ReportedYTDFlags => {
  const out: ReportedYTDFlags = {
    has_medical: false,
    has_rx: false,
    has_total: false,
    combined_total_risk: false,
  };

  for (const record of records) {
    const data = getRecordData(record);
    const medical = parseYtd(data["ytd_medical_paid"]);
    const rx = parseYtd(data["ytd_rx_paid"]);
    const total = parseYtd(data["ytd_total_paid"]);

    if (medical !== null) out.has_medical = true;
    if (rx !== null) out.has_rx = true;
    if (total !== null) out.has_total = true;

    // ADR-077 §8, this record: a total with no separate Rx that exceeds its
    // medical portion (a bare total ⇒ medical 0). An explicit Rx figure means
    // Rx is already attributed, so there is no risk.
    if (!out.combined_total_risk && total !== null && rx === null && total > (medical ?? 0)) {
      out.combined_total_risk = true;
    }
  }

  return out;
};

/**
 * Map the document's reported-YTD flags to the claim-type scope label.
 *
 * PURE — takes flags, not records, so the review surface can label the control
 * from the server's whole-document answer without scanning anything (#6903).
 *
 * Semantics (match the BE `ReportedScope` methods exactly): `assertsMedical =
 * has_medical || (has_total && !has_rx)` — a bare total stands in for medical
 * when no separate Rx is present; `assertsRx = has_rx`. Returns undefined when
 * no YTD component is asserted.
 */
export const reportedScopeFromFlags = (flags: ReportedYTDFlags | undefined): ReportedScope | undefined => {
  if (!flags) return undefined;
  const assertsMedical = flags.has_medical || (flags.has_total && !flags.has_rx);
  const assertsRx = flags.has_rx;
  if (assertsMedical && assertsRx) return "combined";
  if (assertsRx) return "rx";
  if (assertsMedical) return "medical";
  return undefined;
};

/** True when the document maps ANY carrier YTD component. */
export const assertsAnyYtd = (flags: ReportedYTDFlags | undefined): boolean =>
  !!flags && (flags.has_medical || flags.has_rx || flags.has_total);

/**
 * Detects the document's carrier-reported YTD claim-type scope from a COMPLETE
 * record set.
 *
 * Composition of the two functions above, kept so the existing test suite
 * exercises them unchanged — which is what proves the #6903 refactor preserved
 * the predicate rather than merely compiling. Review-path callers must not use
 * this: it scans records. See `detectReportedYTD`.
 */
export const detectReportedScope = (records: ExtractedRecord[]): ReportedScope | undefined =>
  reportedScopeFromFlags(detectReportedYTD(records));

/**
 * ADR-077 §8 double-count risk over a COMPLETE record set. See
 * `detectReportedYTD.combined_total_risk`; retained for the same parity reason
 * as `detectReportedScope` above.
 */
export const detectCombinedTotalRisk = (records: ExtractedRecord[]): boolean =>
  detectReportedYTD(records).combined_total_risk;

/**
 * Get validation errors from a record
 */
export const getRecordValidationErrors = (record: ExtractedRecord): ExtractedRecordError[] => {
  return record.validation_errors || [];
};

/**
 * Get transformation errors from a record
 */
export const getRecordTransformationErrors = (record: ExtractedRecord): ExtractedRecordError[] => {
  return record.transformation_errors || [];
};

/**
 * Deduplicate extracted records by a specified field.
 * Used when the same entity (e.g., a claimant) appears across multiple
 * extracted records due to the worker producing one record per spreadsheet row.
 * Keeps the first occurrence of each unique key.
 * Falls back to an optional secondary field if the primary key is missing.
 * Records with no identifiable key are preserved to avoid data loss.
 */
/**
 * The key `deduplicateRecordsByField` collapses on, or null when the record has
 * no usable key.
 *
 * Extracted so that anything reasoning about "which rows are the same record"
 * uses the SAME key the grid deduped with, instead of a looser one of its own.
 * That divergence was a real bug: the claimant expand panel matched on
 * `claimant_name` alone while the grid deduped on `member_id`, so on a file
 * where names repeat it listed other people's claim lines under one claimant —
 * and the YTD beside them, computed on a stricter identity, correctly covered
 * only one. 50 names across 497,710 members made it unmissable.
 *
 * Null means "never collapses" — matching deduplicateRecordsByField, where an
 * unkeyed record gets a unique synthetic key rather than joining a bucket.
 *
 * `||` rather than `??` is deliberate and load-bearing: a member_id of 0 or ""
 * must fall through to the name.
 */
export const dedupKeyOf = (record: ExtractedRecord, primaryKey: string, fallbackKey?: string): string | null => {
  const data = getRecordData(record);
  const key = data?.[primaryKey] || (fallbackKey ? data?.[fallbackKey] : null);
  return key ? String(key) : null;
};

/**
 * Subscriber-id fields, canonical first. Mirrors claimant.ExplicitSubscriberID
 * (apps/pkg/domains/claimant/household.go) — KEEP IN SYNC.
 */
const SUBSCRIBER_ID_FIELDS = [
  "claimant_related_subscriber_id",
  "related_subscriber_id",
  "subscriber_id",
  "subscriber_number",
] as const;

/** Dashed SSN, mirroring `ssnShapedRE` (claimant/ssn.go). */
const SSN_SHAPED = /\d{3}-\d{2}-\d{4}/;

/** Mirrors relationshipAliases + the enum in claimant/household.go and claimant/types.go — KEEP IN SYNC. */
const RELATIONSHIP_ALIASES = new Map<string, string>([
  ["ee", "self"],
  ["employee", "self"],
  ["subscriber", "self"],
  ["ch", "dependent"],
  ["child", "dependent"],
  ["dep", "dependent"],
  ["sp", "spouse"],
  ["do", "spouse"],
]);
const RELATIONSHIPS = new Set(["self", "dependent", "spouse", "other"]);

/**
 * Mirrors claimant.NormalizeRelationship: "" for a blank or unrecognized value,
 * so "other" only ever means the file said Other.
 */
export const normalizeRelationship = (value: ValidAny): string => {
  if (typeof value !== "string") return "";
  const normalized = value.trim().toLowerCase();
  const alias = RELATIONSHIP_ALIASES.get(normalized);
  if (alias) return alias;
  return RELATIONSHIPS.has(normalized) ? normalized : "";
};

/** Mirrors claimant.ExplicitSubscriberID: strings only, trimmed, SSN-shaped skipped. */
export const explicitSubscriberId = (data: Record<string, ValidAny>): string => {
  for (const field of SUBSCRIBER_ID_FIELDS) {
    const value = data[field];
    if (typeof value === "string" && value.trim() !== "" && !SSN_SHAPED.test(value)) return value.trim();
  }
  return "";
};

/**
 * The household segments identity v2 splits same-name claimants on (#7568):
 * `|subscriber id|relationship`, or "" when the record carries neither, so a
 * record without them keys exactly as before. Read from claimant-schema fields
 * only, as ClaimantWindowIdentity reads them. Mirrors claimantHouseholdSuffix in
 * SummarizeRecords (apps/pkg/domains/document_processing/types.go) — KEEP IN SYNC.
 */
const claimantHouseholdSuffix = (record: ExtractedRecord): string => {
  const data = getRecordData(record, TargetSchema.CLAIMANT);
  const subscriberId = explicitSubscriberId(data).toLowerCase();
  const relationship = normalizeRelationship(data.claimant_relationship);
  return subscriberId || relationship ? `|${subscriberId}|${relationship}` : "";
};

/**
 * The claimant grid's dedupe key — member_id, falling back to claimant_name.
 *
 * On the name fallback the household segments are appended (#7578): the load
 * and the review projection split two same-name people with different
 * subscriber ids or relationships, so the grid, the expand panel and the
 * exclusion cascade must too, or excluding one excludes both.
 */
export const claimantDedupKey = (record: ExtractedRecord): string | null => {
  const key = dedupKeyOf(record, "member_id", "claimant_name");
  if (key === null || getRecordData(record).member_id) return key;
  return key + claimantHouseholdSuffix(record);
};

/** Keeps the first record per key; a null key never collapses. */
export const deduplicateRecordsByKey = (
  records: ExtractedRecord[],
  keyOf: (record: ExtractedRecord) => string | null
): ExtractedRecord[] => {
  const seen = new Map<string, ExtractedRecord>();
  records.forEach((record) => {
    const dedupKey = keyOf(record);
    if (dedupKey !== null) {
      if (!seen.has(dedupKey)) seen.set(dedupKey, record);
    } else {
      seen.set(`__unknown_${seen.size}`, record);
    }
  });
  return Array.from(seen.values());
};

export const deduplicateRecordsByField = (
  records: ExtractedRecord[],
  primaryKey: string,
  fallbackKey?: string
): ExtractedRecord[] => deduplicateRecordsByKey(records, (record) => dedupKeyOf(record, primaryKey, fallbackKey));
